import React from 'react';
import { Quote, Star, CheckCircle2, MessageSquareCode } from 'lucide-react';
import { useLanguage, TestimonialItem } from '../context/LanguageContext';

const TestimonialCard: React.FC<{ testimonial: TestimonialItem }> = ({ testimonial }) => {
  const { t, isUrdu } = useLanguage();

  return (
    <div 
      itemScope 
      itemType="https://schema.org/Review"
      className="w-[320px] sm:w-[380px] md:w-[400px] flex-shrink-0 mx-3 p-6 sm:p-7 rounded-2xl bg-white/95 backdrop-blur-md border border-slate-200/90 hover:border-indigo-500/80 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group relative overflow-hidden cursor-pointer select-none"
      dir={isUrdu ? 'rtl' : 'ltr'}
    >
      {/* Top Ambient Glow Accent */}
      <div className="absolute -top-12 -right-12 w-28 h-28 bg-indigo-500/10 rounded-full blur-xl group-hover:bg-indigo-500/20 transition-all duration-500 pointer-events-none" />
      
      <div>
        {/* Rating & Verified Badge */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
            <meta itemProp="ratingValue" content="5" />
            <meta itemProp="bestRating" content="5" />
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
            ))}
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-full">
            <CheckCircle2 size={11} className="text-emerald-600" />
            <span>{t.testimonials.verifiedBadge || (isUrdu ? 'تصدیق شدہ کلائنٹ' : 'Verified Client')}</span>
          </span>
        </div>

        {/* Quote Content */}
        <p itemProp="reviewBody" className={`text-slate-700 text-xs sm:text-sm leading-relaxed italic mb-6 relative z-10 font-normal ${isUrdu ? 'text-right font-urdu' : 'text-left'}`}>
          "{testimonial.content}"
        </p>
      </div>

      {/* Author Details */}
      <div className="flex items-center gap-3.5 pt-4 border-t border-slate-100 mt-auto" itemProp="author" itemScope itemType="https://schema.org/Person">
        {testimonial.avatar ? (
          <img 
            src={testimonial.avatar} 
            alt={`${testimonial.name} - ${testimonial.role}`} 
            itemProp="image"
            loading="lazy"
            width="40"
            height="40"
            className="w-10 h-10 rounded-full object-cover border-2 border-indigo-100 shadow-xs flex-shrink-0 group-hover:scale-105 transition-transform"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm border border-indigo-200 flex-shrink-0">
            {testimonial.name.charAt(0)}
          </div>
        )}
        <div className={`min-w-0 flex-1 ${isUrdu ? 'text-right' : 'text-left'}`}>
          <h3 itemProp="name" className="text-slate-900 font-bold text-xs sm:text-sm truncate group-hover:text-indigo-600 transition-colors">
            {testimonial.name}
          </h3>
          <p itemProp="jobTitle" className="text-slate-500 text-[11px] font-medium truncate">
            {testimonial.role}
          </p>
        </div>
        <Quote size={20} className="text-indigo-200 flex-shrink-0 group-hover:text-indigo-400 transition-colors" />
      </div>
    </div>
  );
};

const Testimonials: React.FC = () => {
  const { t } = useLanguage();

  const items = t.testimonials.items && t.testimonials.items.length > 0 
    ? t.testimonials.items 
    : [];

  // Split testimonials into 3 sets for the 3 scrolling rows
  const row1 = items.slice(0, 3);
  const row2 = items.slice(3, 6);
  const row3 = items.slice(6, 9);

  // Duplicate each row array to create smooth seamless looping marquee
  const row1Loop = [...row1, ...row1, ...row1, ...row1];
  const row2Loop = [...row2, ...row2, ...row2, ...row2];
  const row3Loop = [...row3, ...row3, ...row3, ...row3];

  return (
    <section 
      id="testimonials" 
      aria-label="Client Reviews & Global Testimonials"
      className="py-16 sm:py-24 bg-slate-50/70 relative overflow-hidden w-full border-t border-slate-200/80"
    >
      {/* Background Decorative Lighting */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-indigo-100/40 blur-[160px] rounded-full pointer-events-none" />

      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 relative z-10 text-center mb-12 sm:mb-16">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white border border-slate-200 rounded-full mb-4 shadow-xs">
          <MessageSquareCode size={14} className="text-indigo-600" />
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-700">
            {t.testimonials.badge}
          </span>
        </div>
        <h2 className="text-3xl sm:text-5xl font-display font-extrabold text-slate-900 tracking-tight">
          {t.testimonials.title}
        </h2>
        <p className="max-w-xl mx-auto text-slate-600 text-xs sm:text-sm mt-3 font-normal leading-relaxed">
          {t.testimonials.subtitle}
        </p>
      </div>

      {/* 3-Row Continuous Marquee Container - LTR forced on track layout for smooth continuous animation */}
      <div className="relative w-full overflow-hidden space-y-6" dir="ltr">
        {/* Left & Right Edge Fade Overlay Masks */}
        <div className="pointer-events-none absolute top-0 bottom-0 left-0 w-16 sm:w-36 bg-gradient-to-r from-slate-50/90 via-slate-50/60 to-transparent z-20" />
        <div className="pointer-events-none absolute top-0 bottom-0 right-0 w-16 sm:w-36 bg-gradient-to-l from-slate-50/90 via-slate-50/60 to-transparent z-20" />

        {/* LINE 1: Leftward Rotation / Scroll */}
        <div className="flex overflow-hidden py-1 w-full" dir="ltr">
          <div className="animate-marquee-left flex flex-nowrap">
            {row1Loop.map((testimonial, idx) => (
              <TestimonialCard key={`row1-${idx}`} testimonial={testimonial} />
            ))}
          </div>
        </div>

        {/* LINE 2: Rightward Rotation / Scroll */}
        <div className="flex overflow-hidden py-1 w-full" dir="ltr">
          <div className="animate-marquee-right flex flex-nowrap">
            {row2Loop.map((testimonial, idx) => (
              <TestimonialCard key={`row2-${idx}`} testimonial={testimonial} />
            ))}
          </div>
        </div>

        {/* LINE 3: Leftward Rotation / Scroll */}
        <div className="flex overflow-hidden py-1 w-full" dir="ltr">
          <div className="animate-marquee-left flex flex-nowrap">
            {row3Loop.map((testimonial, idx) => (
              <TestimonialCard key={`row3-${idx}`} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;

